import React, { useState, useEffect } from 'react';
import { User, Mail, Lock, Bell, Shield, Save, Eye, EyeOff } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import LoadingSpinner from '@/components/common/LoadingSpinner';

export default function Profile() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile form
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    role: ''
  });

  // Password form
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    emailOnSubmission: true,
    emailOnApproval: true,
    emailOnRejection: true,
    emailOnEscalation: true,
    emailOnReopened: true,
    emailOnFinalApproval: true,
    emailWeeklySummary: false
  });

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/me');
      const userData = response.data;
      setUser(userData);
      setProfileForm({
        name: userData.name || '',
        email: userData.email || '',
        role: userData.role || ''
      });
      // Load notification preferences if stored
      setNotifications(userData.preferences?.notifications || notifications);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await api.put('/auth/users/me', {
        name: profileForm.name,
        email: profileForm.email
      });
      toast.success('Profile updated successfully');
      await fetchUserProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    try {
      setSaving(true);
      await api.post('/auth/users/change-password', {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleNotificationUpdate = async () => {
    try {
      setSaving(true);
      await api.put('/auth/users/preferences', {
        notifications
      });
      toast.success('Notification preferences updated');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to update preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" message="Loading profile..." />
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-slate-50 p-6'>
      <div className='max-w-4xl mx-auto'>
        {/* Header */}
        <div className='mb-8'>
          <h1 className='text-3xl font-bold text-slate-900 mb-2'>Account Settings</h1>
          <p className='text-slate-600'>Manage your profile and preferences</p>
        </div>

        {/* Tabs */}
        <div className='bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden'>
          <div className='flex border-b border-slate-200'>
            <button
              onClick={() => setActiveTab('profile')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'profile'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <User className='w-5 h-5' />
              Profile
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'security'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Lock className='w-5 h-5' />
              Security
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex items-center gap-2 px-6 py-4 font-medium transition-colors ${
                activeTab === 'notifications'
                  ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
              }`}
            >
              <Bell className='w-5 h-5' />
              Notifications
            </button>
          </div>

          {/* Tab Content */}
          <div className='p-6'>
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate} className='space-y-6'>
                <div>
                  <label className='block text-sm font-medium text-slate-700 mb-2'>
                    Full Name
                  </label>
                  <input
                    type='text'
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className='w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    required
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-slate-700 mb-2'>
                    Email Address
                  </label>
                  <input
                    type='email'
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                    className='w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                    required
                  />
                </div>

                <div>
                  <label className='block text-sm font-medium text-slate-700 mb-2'>
                    Role
                  </label>
                  <div className='px-4 py-2 bg-slate-100 border border-slate-200 rounded-lg text-slate-600'>
                    {profileForm.role || 'User'}
                    <span className='ml-2 text-xs text-slate-500'>(Cannot be changed)</span>
                  </div>
                </div>

                <div className='flex justify-end'>
                  <button
                    type='submit'
                    disabled={saving}
                    className='flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors shadow-sm'
                  >
                    <Save className='w-4 h-4' />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <form onSubmit={handlePasswordChange} className='space-y-6'>
                <div className='bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6'>
                  <div className='flex items-start gap-3'>
                    <Shield className='w-5 h-5 text-blue-600 mt-0.5' />
                    <div>
                      <h3 className='font-semibold text-blue-900 mb-1'>Password Requirements</h3>
                      <p className='text-sm text-blue-700'>
                        Your password must be at least 8 characters long and contain a mix of letters, numbers, and symbols.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-slate-700 mb-2'>
                    Current Password
                  </label>
                  <div className='relative'>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className='w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      required
                    />
                    <button
                      type='button'
                      onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
                    >
                      {showPasswords.current ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-slate-700 mb-2'>
                    New Password
                  </label>
                  <div className='relative'>
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className='w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      required
                    />
                    <button
                      type='button'
                      onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
                    >
                      {showPasswords.new ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className='block text-sm font-medium text-slate-700 mb-2'>
                    Confirm New Password
                  </label>
                  <div className='relative'>
                    <input
                      type={showPasswords.confirm ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className='w-full px-4 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent'
                      required
                    />
                    <button
                      type='button'
                      onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                      className='absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600'
                    >
                      {showPasswords.confirm ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                    </button>
                  </div>
                </div>

                <div className='flex justify-end'>
                  <button
                    type='submit'
                    disabled={saving}
                    className='flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors shadow-sm'
                  >
                    <Lock className='w-4 h-4' />
                    {saving ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            )}

            {/* Notifications Tab */}
            {activeTab === 'notifications' && (
              <div className='space-y-6'>
                <div>
                  <h3 className='text-lg font-semibold text-slate-900 mb-1'>Email Notifications</h3>
                  <p className='text-sm text-slate-500 mb-5'>Choose which events trigger an email to you. Uncheck any you'd like to opt out of.</p>
                  <div className='space-y-3'>

                    <label className='flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors' data-testid="notif-submission">
                      <div>
                        <p className='font-medium text-slate-900'>New Proposal Submitted</p>
                        <p className='text-sm text-slate-500'>Receive an email when a proposal is submitted for your review (approvers)</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailOnSubmission}
                        onChange={(e) => setNotifications({ ...notifications, emailOnSubmission: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                    <label className='flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors' data-testid="notif-approval">
                      <div>
                        <p className='font-medium text-slate-900'>Proposal Approved</p>
                        <p className='text-sm text-slate-500'>Get notified when your proposal is approved at any stage</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailOnApproval}
                        onChange={(e) => setNotifications({ ...notifications, emailOnApproval: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                    <label className='flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors' data-testid="notif-rejection">
                      <div>
                        <p className='font-medium text-slate-900'>Proposal Rejected</p>
                        <p className='text-sm text-slate-500'>Get notified when your proposal is rejected with reviewer comments</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailOnRejection}
                        onChange={(e) => setNotifications({ ...notifications, emailOnRejection: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                    <label className='flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors' data-testid="notif-escalation">
                      <div>
                        <p className='font-medium text-slate-900'>Approval Escalation</p>
                        <p className='text-sm text-slate-500'>Receive an email when a proposal is escalated to your approval level (CFO / CEO)</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailOnEscalation}
                        onChange={(e) => setNotifications({ ...notifications, emailOnEscalation: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                    <label className='flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors' data-testid="notif-reopened">
                      <div>
                        <p className='font-medium text-slate-900'>Proposal Reopened / More Info Required</p>
                        <p className='text-sm text-slate-500'>Get notified when an approver reopens your proposal for revisions</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailOnReopened}
                        onChange={(e) => setNotifications({ ...notifications, emailOnReopened: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                    <label className='flex items-center justify-between p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors' data-testid="notif-final-approval">
                      <div>
                        <p className='font-medium text-slate-900'>Final Approval</p>
                        <p className='text-sm text-slate-500'>Get notified when your proposal receives all required approvals and is fully approved</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailOnFinalApproval}
                        onChange={(e) => setNotifications({ ...notifications, emailOnFinalApproval: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                    <label className='flex items-center justify-between p-4 bg-blue-50 border border-blue-100 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors' data-testid="notif-weekly-summary">
                      <div>
                        <p className='font-medium text-slate-900'>Weekly Summary Digest</p>
                        <p className='text-sm text-slate-500'>Receive a weekly email summarising your proposal activity (off by default)</p>
                      </div>
                      <input
                        type='checkbox'
                        checked={notifications.emailWeeklySummary}
                        onChange={(e) => setNotifications({ ...notifications, emailWeeklySummary: e.target.checked })}
                        className='w-5 h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500'
                      />
                    </label>

                  </div>
                </div>

                <div className='flex justify-end'>
                  <button
                    onClick={handleNotificationUpdate}
                    disabled={saving}
                    className='flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-colors shadow-sm'
                    data-testid="save-notification-prefs"
                  >
                    <Save className='w-4 h-4' />
                    {saving ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
